import { type ObjectDirective } from 'vue';
import { useTrackingStore, formatLabel } from '@dh-io-owpi/shared/src/stores/tracking';
import { MODIFIER_ACTIVE } from './constants';

type UspEventType = 'click' | 'mouseenter' | 'mouseleave' | 'focus' | 'blur';
const uspEventTypes: UspEventType[] = ['mouseenter', 'mouseleave', 'focus', 'blur', 'click'];
const captureEventTypes: UspEventType[] = ['focus', 'blur'];
export type HoverUspBinding = { index: number; title: string };

export const vHoverUsp: ObjectDirective<HTMLElement, HoverUspBinding> = {
  mounted(el, binding) {
    const handler = (e: MouseEvent | FocusEvent) => toggleActive(e, binding.value);

    uspEventTypes.forEach((type) => el.addEventListener(type, handler, captureEventTypes.includes(type)));

    binding.dir.beforeUnmount = () =>
      uspEventTypes.forEach((type) => el.removeEventListener(type, handler, captureEventTypes.includes(type)));
  },
};

let previousActive: HTMLElement | undefined;
export function toggleActive(
  { type, target, currentTarget, relatedTarget }: MouseEvent | FocusEvent,
  binding: HoverUspBinding,
) {
  const canHover = matchMedia('(pointer: fine)').matches;
  const el = currentTarget as HTMLElement;

  let isActive = false;

  // If the user can hover, we don't want to trigger the active state on click
  if (canHover && type === 'click') return;

  if ((type === 'focus' || type === 'click') && !(target as HTMLElement)?.classList.contains('-footnote')) {
    setActiveElement();
    // If parent element is scrollable, scroll this item into view
    if (el.parentElement && el.parentElement.scrollWidth > el.parentElement.clientWidth)
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  } else if (type === 'blur') {
    // If focus leaves the USP container we remove the active modifier
    if (relatedTarget && !el.parentElement?.contains(relatedTarget as HTMLElement)) {
      el.classList.remove(MODIFIER_ACTIVE);
      previousActive = undefined;
    }
  } else if (canHover) {
    // it will only reach here on mouseenter and mouseleave, set active if mouseenter
    isActive = type === 'mouseenter';
    el.classList.toggle(MODIFIER_ACTIVE, isActive);
  }

  if (isActive) trackUsp(binding);

  function setActiveElement() {
    if (el !== previousActive) {
      previousActive?.classList.remove(MODIFIER_ACTIVE);
      el.classList.add(MODIFIER_ACTIVE);
      previousActive = el;
      isActive = true;
    }
  }
}

function trackUsp(binding: HoverUspBinding) {
  const { trackOnce } = useTrackingStore();

  trackOnce({
    name: 'feature',
    category: 'feature',
    action: 'usp|highlights',
    label: `${binding.index}.${formatLabel(binding.title)}`,
  });
}
